import React from 'react'
import {
  selectCurrentProject,
  selectCurrentContainer,
  computeSceneLength,
  useSequencerStore,
  type Project,
} from '@/stores/sequencerStore'
import { applyScramblerParams } from '@/lib/scramblerRandomizer'
import type { BankId } from '@busswerks/shared'
import { TICKS_PER_STEP } from '@busswerks/shared'
import { useSequencerClock } from '@/hooks/useSequencerClock'
import { useSequencerTriggers } from '@/hooks/useSequencerTriggers'
import { useSyncSequencerProjects } from '@/hooks/useSyncSequencerProjects'
import { useNavigationTracker } from '@/stores/navStore'
import { GlobalMidiProvider } from '@/providers/GlobalMidiProvider'
import { markStep, clearStepMark } from '@/audio/playbackTracker'
import { useNoteRecording } from '@/hooks/useNoteRecording'

const BANK_IDS: readonly BankId[] = ['A', 'B', 'C', 'D']

/**
 * Mount the sequencer clock + trigger pipeline at the route root so
 * playback continues across workspace navigation (/projects/$id →
 * /midi → /slicer). The AudioContext was already module-level shared;
 * this moves the scheduler + firePad handlers to the same "runs for
 * the lifetime of the app" level so the user can start playback in
 * the editor, navigate to the MIDI page, and keep hearing sound.
 *
 * The scheduler reads bpm/stepCount/steps/song directly from the
 * store, which means project switches (via selectProject or
 * selectContainer → selectBank → setActivePatternInBank) take effect
 * immediately without restarting playback. The scheduler effect
 * depends only on the `running` flag; tempo + steps are consumed via
 * refs inside useSequencerClock.
 */
export const PlaybackProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  return (
    <>
      <PlaybackClock />
      <SyncMount />
      <NavTrackerMount />
      <NoteRecordingMount />
      <GlobalMidiProvider />
      {children}
    </>
  )
}

/** Watches the TanStack Router location and records the user's
 *  last visit per workspace into `navStore`, so the
 *  WorkspaceSwitcher can return users to their last-seen URL
 *  rather than the category root. Mounted at the provider level
 *  for the same reason sync is — works on every route, not just
 *  pages that render their own switcher. */
const NavTrackerMount: React.FC = () => {
  useNavigationTracker()
  return null
}

/** Mount the live-MIDI note-recording subscription at the root so
 *  recording works regardless of which page is active. The hook
 *  itself short-circuits when `recordMode === 'off'` or playback
 *  isn't running, so this is cheap while idle. */
const NoteRecordingMount: React.FC = () => {
  useNoteRecording()
  return null
}

/**
 * Mount the container sync hook at the route root so hydrate runs as
 * soon as the user lands anywhere in the app — not only after the
 * SequencerPage mounts. Previously the editor route could get stuck
 * on a "Loading project…" spinner when the user navigated straight
 * to /projects/$id: SequencerPage (which used to own the sync hook)
 * never rendered while the spinner was up, so hydrate never ran and
 * the local containers map never got the server's data.
 */
const SyncMount: React.FC = () => {
  useSyncSequencerProjects()
  return null
}

/**
 * Inner component that mounts the clock. Separated from the provider
 * so the children tree re-renders independently of the scheduler's
 * internal state — the scheduler writes `currentStep` via setTimeout,
 * not via React state, so the whole app doesn't re-render every
 * 25ms.
 */
const PlaybackClock: React.FC = () => {
  const project = useSequencerStore(selectCurrentProject)
  const container = useSequencerStore(selectCurrentContainer)
  const projects = useSequencerStore(s => s.projects)
  const currentSceneIndex = useSequencerStore(s => s.currentSceneIndex)
  const bankMuted = useSequencerStore(s => s.bankMuted)
  const selectScene = useSequencerStore(s => s.selectScene)
  const isPlaying = useSequencerStore(s => s.isPlaying)
  const setCurrentStep = useSequencerStore(s => s.setCurrentStep)

  // Phase 3c — clear the recording tracker when playback stops so
  // a MIDI note arriving while paused doesn't compute against a
  // stale step mark. The next `onStep` fire will re-populate it.
  React.useEffect(() => {
    if (!isPlaying) clearStepMark()
  }, [isPlaying])
  const songMode = useSequencerStore(s => s.songMode)
  const song = useSequencerStore(s => s.song)
  const songCursor = useSequencerStore(s => s.songCursor)
  const setSongCursor = useSequencerStore(s => s.setSongCursor)
  const selectProject = useSequencerStore(s => s.selectProject)
  const setPlaying = useSequencerStore(s => s.setPlaying)
  const applyQueuedSwitches = useSequencerStore(s => s.applyQueuedSwitches)
  const scramblerParams = useSequencerStore(s => s.scramblerParams)

  const { firePad, fireMidiPad, currentTime } = useSequencerTriggers()

  // Song (Scenes × Banks) mode: the `songMode` boolean is the gate.
  // Song mode now coexists with any layoutMode — it stacks the Song
  // grid above the step editor rather than replacing it.
  const scenesMode = songMode && (container?.scenes?.length ?? 0) > 0
  const activeScene =
    scenesMode && container && currentSceneIndex !== null
      ? (container.scenes[currentSceneIndex] ?? null)
      : null

  const advanceScene = React.useCallback(() => {
    if (!container || container.scenes.length === 0) return
    const next = ((currentSceneIndex ?? -1) + 1) % container.scenes.length
    selectScene(next)
  }, [container, currentSceneIndex, selectScene])

  // Song-mode cursor advance — decrement repeats; on zero, jump to
  // the next entry and swap the active pattern. Matches what
  // SequencerPage used to do before this hoist.
  const advanceSongCursor = React.useCallback(() => {
    if (!songMode || song.length === 0) return
    const cursor = songCursor ?? {
      entryIndex: 0,
      repeatsRemaining: Math.max(1, song[0].repeats),
    }
    if (cursor.repeatsRemaining > 1) {
      setSongCursor({
        entryIndex: cursor.entryIndex,
        repeatsRemaining: cursor.repeatsRemaining - 1,
      })
      return
    }
    const nextIndex = (cursor.entryIndex + 1) % song.length
    const nextEntry = song[nextIndex]
    setSongCursor({
      entryIndex: nextIndex,
      repeatsRemaining: Math.max(1, nextEntry.repeats),
    })
    if (project && nextEntry.projectId !== project.id) {
      selectProject(nextEntry.projectId)
      setPlaying(true)
    }
  }, [
    project,
    selectProject,
    setPlaying,
    setSongCursor,
    song,
    songCursor,
    songMode,
  ])

  // Steps-per-measure for the scheduler wrap. In Song (Scenes) mode
  // this is the effective scene length (scene.lengthSteps bumped up
  // to at least the longest assigned pattern, via
  // `computeSceneLength`), so a 32-step pattern inside a stale 16-
  // step scene still plays end-to-end instead of wrapping mid-bar.
  // Outside song mode: the pattern's loop range (loopInStep..loopOutStep
  // inclusive) if both endpoints are set and valid, else the full
  // stepCount.
  const effectiveSceneLength =
    activeScene != null ? computeSceneLength(activeScene, projects) : 16
  // Derive the single-pattern loop window. We keep `loopIn` as the
  // "where step 0 of the clock maps to" offset so `effectiveStep =
  // loopIn + clockStepIndex` inside the fire handler. Odd loop
  // lengths are by design — they produce polyrhythms against
  // anything whose own length doesn't divide the loop.
  const patternStepCount = project?.stepCount ?? 16
  const rawLoopIn = project?.loopInStep
  const rawLoopOut = project?.loopOutStep
  const hasLoopRange =
    typeof rawLoopIn === 'number' &&
    typeof rawLoopOut === 'number' &&
    rawLoopIn >= 0 &&
    rawLoopOut >= rawLoopIn &&
    rawLoopOut < patternStepCount
  const loopIn = hasLoopRange ? rawLoopIn! : 0
  const loopLen = hasLoopRange ? rawLoopOut! - rawLoopIn! + 1 : patternStepCount
  const stepsPerProject = scenesMode ? effectiveSceneLength : loopLen

  useSequencerClock({
    bpm: project?.bpm ?? 120,
    running: isPlaying && !!project,
    currentTime,
    handlers: {
      stepsPerProject,
      onStep: (stepIndex, when) => {
        if (!project) return
        // Phase 3c — scenes mode uses a single scheduler step rate
        // derived from the project's BPM even though individual
        // patterns can differ. Mark once at the scene cadence so
        // the recording hook's position read stays well-defined;
        // recording during scenes-mode is an unusual workflow but
        // we don't want it to silently break.
        if (scenesMode && activeScene && container) {
          const sceneStepDurSec = 60 / (project.bpm ?? 120) / 4
          markStep({
            when,
            stepIndex,
            stepDurSec: sceneStepDurSec,
            stepCount: stepsPerProject,
          })
          // Scene mode: fire all four bank columns in parallel. Each
          // pattern wraps at its own stepCount; the scene's overall
          // length (lengthSteps) controls when we advance to the next
          // scene.
          for (const bankId of BANK_IDS) {
            if (bankMuted[bankId]) continue
            const patternId = activeScene.cells[bankId]
            if (!patternId) continue
            const pat = projects[patternId] as Project | undefined
            if (!pat || pat.stepCount <= 0) continue
            const localStep = stepIndex % pat.stepCount
            // Scene mode honours the playing bank's padBus so the
            // Scrambler's `target: 'bus'` scope can still gate per
            // pad. Pads routed to master fall through as undefined.
            const bankPadBus: Record<number, string> =
              container?.banks[bankId]?.padBus ?? {}
            for (let padIdx = 0; padIdx < 16; padIdx++) {
              const rawCell = pat.steps[padIdx]?.[localStep]
              if (!rawCell?.on) continue
              // Scrambler runs on the fire path so the stored
              // pattern stays pristine. Returns null on probability
              // drops (the step is scheduled but silent).
              const cell = applyScramblerParams(
                rawCell,
                scramblerParams,
                padIdx,
                localStep,
                pat.stepCount,
                pat.steps[padIdx],
                bankPadBus[padIdx],
              )
              if (!cell) continue
              const padDef = pat.padDefs?.[padIdx]
              const padEnv =
                padDef &&
                (padDef.kind === 'sample' ||
                  padDef.kind === 'synth' ||
                  padDef.kind === 'granular')
                  ? padDef.envelope
                  : undefined
              const envelope =
                cell.envelope || padEnv
                  ? { ...padEnv, ...cell.envelope }
                  : undefined
              // Phase 3a — each pattern in a scene has its own BPM,
              // so stepDurSec must be derived per-pattern.
              const patStepDurSec = 60 / (pat.bpm ?? 120) / 4
              const offsetSec =
                ((cell.microOffset ?? 0) > 0 && (cell.microOffset ?? 0) < 1
                  ? cell.microOffset!
                  : 0) * patStepDurSec
              firePad(padIdx, when + offsetSec, {
                velocity: cell.velocity,
                pitch: cell.pitch,
                fx: cell.fx,
                length: cell.length,
                reverse: cell.reverse,
                timeStretch: cell.timeStretch,
                envelope,
                roll: cell.roll,
                stepIndex: localStep,
              })
            }
            // Piano-roll notes that land inside this step's tick
            // window also fire in scene-layout mode. `patStepDurSec`
            // is already in scope for the MIDI branch below; reuse
            // it.
            const sceneStepStartTick = localStep * TICKS_PER_STEP
            const sceneStepEndTick = sceneStepStartTick + TICKS_PER_STEP
            const scenePatStepDurSec = 60 / (pat.bpm ?? 120) / 4
            for (const note of pat.notes ?? []) {
              if (
                note.tickStart < sceneStepStartTick ||
                note.tickStart >= sceneStepEndTick
              ) {
                continue
              }
              const subStepRatio =
                (note.tickStart - sceneStepStartTick) / TICKS_PER_STEP
              firePad(note.padIndex, when + subStepRatio * scenePatStepDurSec, {
                velocity: note.velocity,
                pitch: note.pitchOffset,
                length: note.tickLength / TICKS_PER_STEP,
                stepIndex: localStep,
              })
            }
            const patStepDurSecForMidi = 60 / (pat.bpm ?? 120) / 4
            for (const mp of pat.midiPads) {
              const cell = pat.midiPadSteps[mp.id]?.[localStep]
              if (!cell?.on) continue
              const midiOffsetSec =
                ((cell.microOffset ?? 0) > 0 && (cell.microOffset ?? 0) < 1
                  ? cell.microOffset!
                  : 0) * patStepDurSecForMidi
              fireMidiPad(mp, when + midiOffsetSec, {
                velocity: cell.velocity,
                length: cell.length,
              })
            }
          }
          const uiDelayMs = Math.max(0, (when - currentTime()) * 1000)
          window.setTimeout(() => setCurrentStep(stepIndex), uiDelayMs)
          if (stepIndex === effectiveSceneLength - 1) {
            const delayMs = uiDelayMs
            window.setTimeout(() => advanceScene(), delayMs + 5)
          }
          return
        }

        // Single-pattern mode (the default — 2×8 / 8×8 / 4×4 / Tracker).
        // `stepIndex` here is the clock's 0..loopLen-1 cursor, which
        // we offset into the pattern's real step space so row-range
        // looping (loopIn..loopOut) maps transparently.
        const effectiveStep = loopIn + stepIndex
        // Pattern swing — delay odd-numbered steps by
        // `stepSec × swing × 0.5`. 0.5 is the standard cap because
        // swing=1 would move the "and" onto the next downbeat; cap
        // keeps the groove interpretable at all settings.
        const stepDurSec = 60 / (project.bpm ?? 120) / 4
        const swingAmount = project.swing ?? 0
        const swingDelaySec =
          swingAmount > 0 && stepIndex % 2 === 1
            ? stepDurSec * swingAmount * 0.5
            : 0
        const firedWhen = when + swingDelaySec
        // Phase 3c — record the step boundary so the note-
        // recording hook can compute the fractional position of
        // an incoming MIDI note. `stepsPerProject` is the loop
        // length the scheduler is counting through, so recording
        // wraps the same way playback does. Marked at `firedWhen`
        // (post-swing) so recorded microOffsets match what the
        // user actually heard.
        markStep({
          when: firedWhen,
          stepIndex: effectiveStep,
          stepDurSec,
          stepCount: stepsPerProject,
        })
        // Active-bank padBus map — needed by the Scrambler's
        // `target: 'bus'` scope so only pads routed through the
        // attached bus get transformed. Recomputed per-tick so
        // bus re-routing takes effect on the next scheduler
        // boundary.
        const activeBankId = container?.activeBankId ?? null
        const activeBankPadBus: Record<number, string> =
          (activeBankId ? container?.banks[activeBankId]?.padBus : undefined) ??
          {}
        for (let padIdx = 0; padIdx < 16; padIdx++) {
          const rawCell = project.steps[padIdx]?.[effectiveStep]
          if (!rawCell?.on) continue
          const cell = applyScramblerParams(
            rawCell,
            scramblerParams,
            padIdx,
            effectiveStep,
            project.stepCount,
            project.steps[padIdx],
            activeBankPadBus[padIdx],
          )
          if (!cell) continue
          const padDef = project.padDefs?.[padIdx]
          const padEnv =
            padDef && (padDef.kind === 'sample' || padDef.kind === 'synth')
              ? padDef.envelope
              : undefined
          const envelope =
            cell.envelope || padEnv
              ? { ...padEnv, ...cell.envelope }
              : undefined
          // Phase 3a — off-grid notes: microOffset shifts the fire
          // time within the step without moving the cell's home
          // index. Clamped at write time; read defensively here too.
          const offsetSec =
            ((cell.microOffset ?? 0) > 0 && (cell.microOffset ?? 0) < 1
              ? cell.microOffset!
              : 0) * stepDurSec
          firePad(padIdx, firedWhen + offsetSec, {
            velocity: cell.velocity,
            pitch: cell.pitch,
            fx: cell.fx,
            length: cell.length,
            reverse: cell.reverse,
            timeStretch: cell.timeStretch,
            envelope,
            roll: cell.roll,
            stepIndex: effectiveStep,
          })
        }
        // Wave 5 piano-roll notes that land inside this step's
        // tick window. One step = TICKS_PER_STEP ticks; any note
        // whose `tickStart` is in `[stepStartTick, stepStartTick +
        // TICKS_PER_STEP)` fires at the sub-step offset within the
        // step. Note length becomes the fire gate so sustain
        // follows the authored length instead of a single
        // step-sized hit.
        const noteStepStartTick = effectiveStep * TICKS_PER_STEP
        const noteStepEndTick = noteStepStartTick + TICKS_PER_STEP
        for (const note of project.notes ?? []) {
          if (
            note.tickStart < noteStepStartTick ||
            note.tickStart >= noteStepEndTick
          ) {
            continue
          }
          const subStepRatio =
            (note.tickStart - noteStepStartTick) / TICKS_PER_STEP
          firePad(note.padIndex, firedWhen + subStepRatio * stepDurSec, {
            velocity: note.velocity,
            pitch: note.pitchOffset,
            length: note.tickLength / TICKS_PER_STEP,
            stepIndex: effectiveStep,
          })
        }
        // Custom MIDI overflow pads.
        for (const mp of project.midiPads) {
          const cell = project.midiPadSteps[mp.id]?.[effectiveStep]
          if (!cell?.on) continue
          const midiOffsetSec =
            ((cell.microOffset ?? 0) > 0 && (cell.microOffset ?? 0) < 1
              ? cell.microOffset!
              : 0) * stepDurSec
          fireMidiPad(mp, firedWhen + midiOffsetSec, {
            velocity: cell.velocity,
            length: cell.length,
          })
        }
        // Paint the playhead on the UI thread at the matching audio
        // time so what the user sees lines up with what they hear.
        // Paint the effective pattern step so the tracker's playhead
        // highlights the row that's actually firing — not the 0..loopLen
        // clock counter.
        const uiDelayMs = Math.max(0, (when - currentTime()) * 1000)
        window.setTimeout(() => setCurrentStep(effectiveStep), uiDelayMs)

        // Pattern wrap boundary: apply any queued bank/pattern switches
        // so they land on the downbeat of the next loop ("on the 1"),
        // and advance the legacy song cursor if the old flat-list
        // songMode is still on (Scenes mode handles its own wrap
        // above). For row-range loops the wrap happens at the range
        // end rather than the pattern end — users expect queued
        // switches to land on the *perceived* loop point.
        if (stepIndex === stepsPerProject - 1) {
          const delayMs = Math.max(0, (when - currentTime()) * 1000)
          window.setTimeout(() => applyQueuedSwitches(), delayMs + 5)
          if (songMode && song.length > 0) {
            window.setTimeout(() => advanceSongCursor(), delayMs + 5)
          }
        }
      },
    },
  })

  return null
}
